<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <div v-else>
      <!-- SDP認証情報はタブ外の SdpCredentialsCard へ移設（キッチン/レシート共用のため） -->

      <!-- プリンター管理セクション -->
      <v-card class="mb-8">
        <v-card-title class="d-flex justify-space-between align-center">
          キッチンプリンター
          <v-dialog
            v-model="isPrinterFormOpen"
            width="500"
            persistent
          >
            <template #activator="{ on, attrs }">
              <v-btn
                fab
                small
                :ripple="false"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                {{ printerForm.id ? 'プリンター編集' : '新規プリンター' }}
              </v-card-title>

              <v-card-text>
                <v-text-field
                  v-model="printerForm.name"
                  label="プリンター名"
                  :error="!printerForm.name"
                  placeholder="例: キッチン、ドリンカー"
                />

                <v-text-field
                  v-model="printerForm.ipAddress"
                  label="IPアドレス"
                  :error="!printerForm.ipAddress"
                  placeholder="192.168.1.20"
                  minlength="7"
                  maxlength="15"
                  :rules="[
                    value => /^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/.test(value) || '無効なIPアドレスです',
                  ]"
                />

                <v-text-field
                  v-model.number="printerForm.position"
                  label="表示順"
                  type="number"
                  placeholder="0"
                />

                <v-divider class="my-4" />

                <div class="text-subtitle-2 mb-2">
                  サーバーダイレクトプリント（SDP）
                </div>

                <v-switch
                  v-model="printerForm.sdpEnabled"
                  label="SDPで印刷する"
                  hint="ONにすると注文をサーバー経由でこのプリンターに送信します"
                  persistent-hint
                  inset
                  dense
                />

                <v-text-field
                  v-model="printerForm.sdpDeviceId"
                  label="デバイスID（devid）"
                  placeholder="local_printer"
                  hint="Hub機自身は local_printer。2台目以降はWebConfigで登録したデバイスID"
                  persistent-hint
                  class="mt-2"
                />

                <!-- SDP認証情報（このプリンタ単位）。保存済みプリンタのみ発行できる。別フロア/別ネットワークは別のIDになる -->
                <div
                  v-if="printerForm.sdpEnabled && printerForm.id"
                  class="mt-4 pa-3"
                  style="background: rgba(0,0,0,0.03); border-radius: 4px;"
                >
                  <div class="text-body-2 mb-1">
                    ポーリングID:
                    <strong style="font-family: monospace;">{{ printerForm.sdpPollingId || '未発行' }}</strong>
                    <v-icon
                      v-if="printerForm.sdpPollingId"
                      small
                      class="ml-1"
                      style="cursor: pointer;"
                      @click="copyToClipboard(printerForm.sdpPollingId, 'ポーリングID')"
                    >
                      {{ icons.mdiContentCopy }}
                    </v-icon>
                  </div>
                  <div
                    v-if="issuedPassword"
                    class="text-body-2 mb-2"
                  >
                    パスワード:
                    <strong style="font-family: monospace;">{{ isSdpPasswordVisible ? issuedPassword : '••••••••' }}</strong>
                    <v-icon
                      small
                      class="ml-1"
                      style="cursor: pointer;"
                      @click="isSdpPasswordVisible = !isSdpPasswordVisible"
                    >
                      {{ isSdpPasswordVisible ? icons.mdiEyeOff : icons.mdiEye }}
                    </v-icon>
                    <v-icon
                      small
                      class="ml-1"
                      style="cursor: pointer;"
                      @click="copyToClipboard(issuedPassword, 'パスワード')"
                    >
                      {{ icons.mdiContentCopy }}
                    </v-icon>
                  </div>
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    :ripple="false"
                    :loading="isIssuing"
                    @click="onClickIssueSdpCredentials"
                  >
                    ID・パスワードを{{ printerForm.sdpPollingId ? '再発行' : '発行' }}
                  </v-btn>
                  <div class="text-caption text--secondary mt-1">
                    上のサーバーURLとこのID・パスワードをプリンタのWebConfig（サーバー認証）に設定します。再発行すると既存値は無効になります。
                  </div>

                  <!-- 再発行の確認ダイアログ（誤タップによる意図しないリセット防止） -->
                  <v-dialog
                    v-model="isReissueConfirmOpen"
                    max-width="420"
                    persistent
                  >
                    <v-card>
                      <v-card-title>ID・パスワードを再発行しますか？</v-card-title>
                      <v-card-text>
                        再発行すると現在のポーリングID・パスワードは<strong>すぐに無効</strong>になり、このプリンタからの印刷が止まります。<br>
                        再発行後は、プリンタのWebConfig（サーバー認証）に新しいID・パスワードを設定し直してください。
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer />
                        <v-btn
                          text
                          :ripple="false"
                          :disabled="isIssuing"
                          @click="isReissueConfirmOpen = false"
                        >
                          キャンセル
                        </v-btn>
                        <v-btn
                          color="error"
                          :ripple="false"
                          :loading="isIssuing"
                          @click="confirmReissueSdpCredentials"
                        >
                          再発行する
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </div>
                <p
                  v-else-if="printerForm.sdpEnabled && !printerForm.id"
                  class="text-caption text--secondary mt-2 mb-0"
                >
                  保存後に、この画面でSDP認証情報（ポーリングID・パスワード）を発行できます。
                </p>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  :ripple="false"
                  :disabled="!isPrinterFormValid"
                  :loading="isSubmitting"
                  @click="submitPrinter"
                >
                  {{ printerForm.id ? '更新' : '作成' }}
                </v-btn>

                <v-btn
                  :ripple="false"
                  color="blue darken-1"
                  text
                  @click="cancelPrinterForm"
                >
                  キャンセル
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-title>

        <v-card-subtitle
          v-if="sdpServerUrl"
          class="d-flex align-center pt-2 flex-wrap"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiAccessPointNetwork }}
          </v-icon>
          <span>
            SDPサーバーURL:
            <strong style="font-family: monospace;">{{ sdpServerUrl }}</strong>
            <v-icon
              small
              class="ml-1"
              style="cursor: pointer;"
              @click="copyToClipboard(sdpServerUrl, 'サーバーURL')"
            >
              {{ icons.mdiContentCopy }}
            </v-icon>
          </span>
        </v-card-subtitle>

        <v-card-text>
          <!-- 全プリンタがSDP（ブラウザ直結印刷の対象が無い）場合、端末選択も接続テストも無意味なので行ごと非表示 -->
          <div
            v-if="hasBrowserPrinters"
            class="d-flex align-center mb-2"
          >
            <span class="text-caption text--secondary">この端末で印刷に使用するプリンタにチェックを入れてください</span>
            <v-spacer />
            <v-btn
              small
              text
              :loading="isAutoDetecting"
              @click="onRetestAll"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiRefresh }}
              </v-icon>
              使用プリンタ再テスト
            </v-btn>
          </div>
          <v-data-table
            :headers="printerHeaders"
            :items="kitchenPrinters"
            :header-props="{ sortByText: 'ソート' }"
            hide-default-footer
            disable-pagination
            no-data-text="プリンターが登録されていません"
          >
            <template #[`item.enabled`]="{item}">
              <!-- SDP有効プリンタはサーバーが印刷するため、端末ごとの「この端末で使用」選択は無関係。
                   紛らわしいのでチェックボックスは出さず "—" を表示する。 -->
              <div
                v-if="item.attributes.sdpEnabled"
                class="d-flex align-center justify-center text--disabled"
              >
                —
              </div>
              <div
                v-else
                class="d-flex align-center justify-center"
              >
                <v-checkbox
                  :input-value="isPrinterEnabled(item.id)"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                  @change="onTogglePrinter(item.id)"
                />
                <v-progress-circular
                  v-if="connectionStatuses[item.id] === 'checking'"
                  indeterminate
                  size="16"
                  width="2"
                />
                <v-icon
                  v-else-if="connectionStatuses[item.id] === 'connected'"
                  small
                  color="success"
                >
                  {{ icons.mdiCheckCircle }}
                </v-icon>
                <v-icon
                  v-else-if="connectionStatuses[item.id] === 'failed'"
                  small
                  color="error"
                >
                  {{ icons.mdiAlertCircle }}
                </v-icon>
              </div>
            </template>

            <template #[`item.sdp`]="{item}">
              <v-chip
                v-if="item.attributes.sdpEnabled"
                x-small
                color="success"
                outlined
              >
                SDP有効
              </v-chip>
              <v-chip
                v-else
                x-small
                color="default"
                outlined
              >
                無効
              </v-chip>
              <span
                v-if="item.attributes.sdpEnabled"
                class="text-caption text--secondary ml-2"
              >
                {{ item.attributes.sdpDeviceId }}
              </span>
            </template>

            <template #[`item.lastPrint`]="{item}">
              <div v-if="item.attributes.lastPrintStatus">
                <v-chip
                  x-small
                  :color="lastPrintColor(item.attributes.lastPrintStatus)"
                  outlined
                >
                  {{ lastPrintLabel(item.attributes.lastPrintStatus) }}
                </v-chip>
                <div class="text-caption text--secondary">
                  {{ formatDateTime(item.attributes.lastPrintedAt) }}
                </div>
              </div>
              <span
                v-else
                class="text--disabled"
              >—</span>
            </template>

            <template #[`item.actions`]="{item}">
              <span class="d-flex align-center justify-end">
                <v-btn
                  x-small
                  outlined
                  color="grey darken-1"
                  class="mr-1"
                  :href="webConfigUrl(item)"
                  :disabled="!webConfigUrl(item)"
                  target="_blank"
                >
                  WebConfig
                </v-btn>

                <v-btn
                  v-if="!item.attributes.sdpEnabled"
                  x-small
                  outlined
                  color="primary"
                  class="mr-1"
                  :loading="testingPrinterId === item.id"
                  @click="testConnection(item)"
                >
                  接続テスト
                </v-btn>

                <v-btn
                  v-if="!item.attributes.sdpEnabled"
                  x-small
                  outlined
                  color="success"
                  class="mr-1"
                  @click="openDiagnostic(item)"
                >
                  診断
                </v-btn>

                <v-btn
                  x-small
                  outlined
                  color="info"
                  class="mr-1"
                  @click="showPreview(item)"
                >
                  プレビュー
                </v-btn>

                <v-btn
                  v-if="!item.attributes.sdpEnabled"
                  x-small
                  outlined
                  color="secondary"
                  class="mr-1"
                  :loading="testPrintingId === item.id"
                  @click="testPrint(item)"
                >
                  テスト印刷
                </v-btn>

                <v-btn
                  icon
                  :ripple="false"
                  @click="editPrinter(item)"
                >
                  <v-icon small>
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                </v-btn>

                <yona-edit-dialog
                  title="削除の確認"
                  btn-color="error"
                  save-text="同意の上で削除"
                  @save="destroyPrinter(item.id)"
                >
                  <template #append-outer-display-name>
                    <v-btn
                      icon
                      :ripple="false"
                    >
                      <v-icon small>
                        {{ icons.mdiTrashCanOutline }}
                      </v-icon>
                    </v-btn>
                  </template>

                  <template #input>
                    <span>
                      「{{ item.attributes.name }}」を削除してもよろしいですか
                    </span>
                  </template>
                </yona-edit-dialog>
              </span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- プレビューダイアログ -->
      <v-dialog
        v-model="isPreviewOpen"
        max-width="400"
      >
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ previewPrinterName }} - 印刷プレビュー</span>
            <v-btn
              icon
              @click="isPreviewOpen = false"
            >
              <v-icon>
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div
              v-for="(preview, index) in previewSlips"
              :key="index"
              class="mb-4"
            >
              <v-chip
                small
                class="mb-2"
              >
                {{ preview.label }}
              </v-chip>
              <div
                class="preview-svg-container"
                v-html="preview.svg"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- プリンタ診断ダイアログ -->
      <kitchen-printer-diagnostic-dialog
        v-model="isDiagnosticOpen"
        :printer-name="diagnosticTarget.name"
        :ip-address="diagnosticTarget.ipAddress"
      />

      <!-- 振り分け設定セクション -->
      <v-card>
        <v-card-title>
          カテゴリ振り分け設定
        </v-card-title>

        <v-card-text>
          <div
            v-if="vendibleTags.length === 0"
            class="text--secondary"
          >
            商品タグが登録されていません
          </div>

          <div
            v-for="tag in vendibleTags"
            :key="tag.id"
            class="d-flex align-center mb-3"
          >
            <span
              class="mr-4 text-body-1"
              style="min-width: 120px;"
            >
              {{ tag.attributes.name }}
            </span>
            <v-select
              v-model="routingMap[tag.id]"
              :items="printerSelectItems"
              item-value="id"
              item-text="name"
              clearable
              dense
              hide-details
              placeholder="印刷しない"
              no-data-text="プリンターがありません"
              :menu-props="{ auto: true, offsetY: true }"
              style="max-width: 250px;"
            />
          </div>

          <div
            v-if="vendibleTags.length > 0"
            class="d-flex justify-end mt-4"
          >
            <v-btn
              color="primary"
              :loading="isSavingRoutings"
              @click="saveRoutings"
            >
              振り分け設定を保存
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiClose,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiRefresh,
  mdiAccessPointNetwork,
  mdiContentCopy,
  mdiEye,
  mdiEyeOff,
} from '@mdi/js'
import { findIndex } from 'lodash'
import { PrinterConnection } from '@yonarezi/yonarezi3.epos-printer'
import { useKitchenSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import KitchenPrinterApi from '@/api/waiter/KitchenPrinter'
import VendibleTagApi from '@/api/waiter/VendibleTag'
import useCompInit from '@/views/composable/useCompInit'
import { buildKitchenSlipLines } from '@/views/composable/kitchenSlipBreakdown'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { reportPrinterError } from '@/utils/sentryReportPrinterError'
import useCurrentData from '@/views/composable/useCurrentData'
import KitchenPrinterDiagnosticDialog from '@/views/components/printer/KitchenPrinterDiagnosticDialog.vue'
import useKitchenPrinterSelection from '@/views/composable/useKitchenPrinterSelection'

export default {
  components: {
    YonaEditDialog,
    KitchenPrinterDiagnosticDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const { currentClub } = useCurrentData()
    const isPrinterFormOpen = ref(false)
    const isSubmitting = ref(false)
    const isSavingRoutings = ref(false)
    const testingPrinterId = ref(null)
    const testPrintingId = ref(null)
    const isPreviewOpen = ref(false)
    const previewSlips = ref([])
    const previewPrinterName = ref('')
    const kitchenPrinters = ref([])
    // ブラウザ直結印刷の対象（=非SDP）プリンタが1台でもあるか。
    // 無い（全SDP）場合は端末選択・接続テストのUIを出さない。
    const hasBrowserPrinters = computed(() => kitchenPrinters.value.some(p => !p.attributes?.sdpEnabled))
    const vendibleTags = ref([])
    const routingMap = reactive({})
    const sdpServerUrl = ref('')
    const isDiagnosticOpen = ref(false)
    const diagnosticTarget = reactive({ name: '', ipAddress: '' })
    const openDiagnostic = item => {
      diagnosticTarget.name = item.attributes.name
      diagnosticTarget.ipAddress = item.attributes.ipAddress
      isDiagnosticOpen.value = true
    }
    const webConfigUrl = item => {
      const ip = removePortFromIpAddress(item.attributes?.ipAddress || '')
      if (!ip) return null

      return `https://${ip}/`
    }

    // 端末ごとの「印刷に使用するプリンタ」選択（OrdersNew の印刷ドロップダウンと localStorage を共有）
    const {
      isPrinterEnabled, togglePrinter, connectionStatuses,
      isAutoDetecting, autoDetectPrinters,
    } = useKitchenPrinterSelection()
    const onTogglePrinter = printerId => {
      const allPrinterIds = kitchenPrinters.value.map(p => p.id)
      togglePrinter(printerId, allPrinterIds)
    }
    const onRetestAll = () => {
      autoDetectPrinters(kitchenPrinters.value)
    }

    const printerForm = reactive({
      id: null,
      name: '',
      ipAddress: '',
      position: 0,
      sdpEnabled: false,
      sdpDeviceId: 'local_printer',
      sdpPollingId: null,
    })
    // 認証情報発行の状態（プリンタ単位）
    const isIssuing = ref(false)
    const issuedPassword = ref(null)
    const isSdpPasswordVisible = ref(false)
    const isReissueConfirmOpen = ref(false)

    const isPrinterFormValid = computed(() => {
      return printerForm.name && printerForm.ipAddress
        && /^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/.test(printerForm.ipAddress)
    })

    const printerSelectItems = computed(() => {
      return kitchenPrinters.value.map(p => ({
        id: String(p.id),
        name: p.attributes.name,
      }))
    })

    const cancelPrinterForm = () => {
      isPrinterFormOpen.value = false
      printerForm.id = null
      printerForm.name = ''
      printerForm.ipAddress = ''
      printerForm.position = 0
      printerForm.sdpEnabled = false
      printerForm.sdpDeviceId = 'local_printer'
      printerForm.sdpPollingId = null
      issuedPassword.value = null
      isSdpPasswordVisible.value = false
    }

    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text)
        vm.$toast.success(`${label}をコピーしました`)
      } catch {
        vm.$toast.error('コピーに失敗しました')
      }
    }

    // このプリンタの SDP認証情報（polling_id / password）を発行する
    const issueSdpCredentials = async () => {
      if (!printerForm.id) return
      isIssuing.value = true
      try {
        const { data } = await KitchenPrinterApi.issueSdpCredentials(printerForm.id)
        printerForm.sdpPollingId = data.sdpPollingId
        issuedPassword.value = data.sdpPassword
        isSdpPasswordVisible.value = true
        vm.$toast.success('SDP認証情報を発行しました')
      } catch {
        vm.$toast.error('SDP認証情報の発行に失敗しました')
      } finally {
        isIssuing.value = false
      }
    }

    // 既に発行済みなら確認ダイアログを挟む（誤タップで既存値が無効化される事故が多発したため）
    const onClickIssueSdpCredentials = () => {
      if (printerForm.sdpPollingId) {
        isReissueConfirmOpen.value = true
      } else {
        issueSdpCredentials()
      }
    }

    const confirmReissueSdpCredentials = async () => {
      await issueSdpCredentials()
      isReissueConfirmOpen.value = false
    }

    const editPrinter = item => {
      isPrinterFormOpen.value = true
      printerForm.id = item.id
      printerForm.name = item.attributes.name
      printerForm.ipAddress = item.attributes.ipAddress
      printerForm.position = item.attributes.position || 0
      printerForm.sdpEnabled = item.attributes.sdpEnabled || false
      printerForm.sdpDeviceId = item.attributes.sdpDeviceId || 'local_printer'
      printerForm.sdpPollingId = item.attributes.sdpPollingId || null
      issuedPassword.value = null
      isSdpPasswordVisible.value = false
    }

    const submitPrinter = async () => {
      isSubmitting.value = true
      const isUpdate = !!printerForm.id
      const fnVerb = isUpdate ? 'update' : 'create'
      const {
        id, name, ipAddress, position, sdpEnabled, sdpDeviceId,
      } = printerForm

      const res = await KitchenPrinterApi[`${fnVerb}KitchenPrinter`]({
        id, name, ipAddress, position, sdpEnabled, sdpDeviceId,
      })

      if (res?.data) {
        if (isUpdate) {
          const idx = findIndex(kitchenPrinters.value, o => +o.id === +res.data.kitchenPrinter.data.id)
          kitchenPrinters.value.splice(idx, 1, res.data.kitchenPrinter.data)
        } else {
          kitchenPrinters.value.push(res.data.kitchenPrinter.data)
        }
        vm.$toast.success(isUpdate ? 'プリンターを更新しました' : 'プリンターを追加しました')
      }

      isSubmitting.value = false
      cancelPrinterForm()
    }

    const destroyPrinter = async id => {
      const res = await KitchenPrinterApi.deleteKitchenPrinter(id)

      if (res?.data?.status === 'error') {
        vm.$toast.error('削除できません')

        return
      }

      if (res) {
        const idx = findIndex(kitchenPrinters.value, o => +o.id === +id)
        kitchenPrinters.value.splice(idx, 1)
        vm.$toast.success('プリンターを削除しました')
      }
    }

    const testConnection = async item => {
      testingPrinterId.value = item.id
      const ipAddress = removePortFromIpAddress(item.attributes.ipAddress)
      try {
        const conn = new PrinterConnection({ ipAddress })
        const printer = await conn.connect()
        if (printer?.ePosDev?.isConnected()) {
          vm.$toast.success(`${item.attributes.name}: 接続成功`)
        } else {
          vm.$toast.error(`${item.attributes.name}: 接続失敗`)
        }
      } catch (error) {
        reportPrinterError(error, {
          kind: 'kitchen',
          phase: 'connect-test',
          ipAddress,
          clubId: currentClub.value?.id,
          printerName: item.attributes?.name,
        })
        vm.$toast.error(`${item.attributes.name}: 接続失敗`)
      } finally {
        testingPrinterId.value = null
      }
    }

    const testPrint = async item => {
      testPrintingId.value = item.id
      try {
        const ipAddress = removePortFromIpAddress(item.attributes.ipAddress)
        // 実際の伝票と同じ規則(buildKitchenSlipLines)で、中身の内訳と備考が出るサンプルを組み立てる
        const { recipientNames, itemRemark } = buildKitchenSlipLines(
          [
            { names: ['さくら'], optionsText: 'ビール' },
            { names: ['みさき'], optionsText: 'ハイボール' },
            { names: ['あやか'], optionsText: 'ビール' },
          ],
          '辛さ控えめ',
        )
        const options = computed(() => ({
          ipAddress,
          orderedAt: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }),
          staffName: 'テスト太郎',
          hallName: 'ホールA',
          tableCode: 'A-01',
          customerCount: 3,
          itemName: 'テスト商品A',
          itemQuantity: 3,
          itemRemark,
          recipientNames,
        }))

        const { send, onReceive } = useKitchenSlipPrint(options)
        onReceive.value = response => {
          if (response.success) {
            vm.$toast.success(`${item.attributes.name}: テスト印刷成功`)
          } else {
            vm.$toast.error(`${item.attributes.name}: テスト印刷失敗`)
          }
        }
        await send()
      } catch {
        vm.$toast.error(`${item.attributes.name}: テスト印刷失敗`)
      } finally {
        testPrintingId.value = null
      }
    }

    const showPreview = item => {
      const orderedAt = new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
      const baseOptions = {
        ipAddress: '0.0.0.0',
        orderedAt,
        staffName: 'テスト太郎',
        hallName: 'ホールA',
        tableCode: 'A-01',
        customerCount: 3,
      }

      // 商品別のサンプルデータ。明細1件ごとの受領者名・中身から、印刷と同じ規則(buildKitchenSlipLines)で行を組み立てる
      const sampleSlips = [
        {
          label: '商品A（複数受領者）',
          itemName: 'テスト商品A',
          orderEntries: [
            { names: ['さくら'], optionsText: '' },
            { names: ['あやか'], optionsText: '' },
            { names: ['みさき'], optionsText: '' },
          ],
        },
        {
          label: '商品B（備考あり）',
          itemName: 'テスト商品B',
          remark: '辛さ控えめ',
          orderEntries: [{ names: ['さくら'], optionsText: '' }],
        },
        {
          label: '商品C（受領者なし）',
          itemName: 'テスト商品C',
          orderEntries: [
            { names: ['ゲスト'], optionsText: '' },
            { names: ['ゲスト'], optionsText: '' },
          ],
        },
        {
          label: '商品D（全員同じオプション）',
          itemName: 'テスト商品D',
          orderEntries: [
            { names: ['さくら'], optionsText: 'コーラ / 濃いめ' },
            { names: ['あやか'], optionsText: 'コーラ / 濃いめ' },
          ],
        },
        {
          label: '商品E（オプション別の内訳）',
          itemName: 'テスト商品E',
          orderEntries: [
            { names: ['さくら'], optionsText: 'ビール' },
            { names: ['みさき'], optionsText: 'ハイボール' },
            { names: ['あやか'], optionsText: 'ビール' },
            { names: ['さくら'], optionsText: 'ビール' },
          ],
        },
        {
          label: '商品F（ボトルを複数人で受領）',
          itemName: 'テスト商品F',
          orderEntries: [
            { names: ['さくら', 'あやか', 'みさき'], optionsText: '水割り' },
            { names: ['さくら', 'あやか', 'みさき'], optionsText: '水割り' },
          ],
        },
      ]

      previewSlips.value = sampleSlips.map(slip => {
        const { recipientNames, itemRemark } = buildKitchenSlipLines(slip.orderEntries, slip.remark)
        const options = computed(() => ({
          ...baseOptions,
          itemName: slip.itemName,
          itemQuantity: slip.orderEntries.length,
          itemRemark,
          recipientNames,
        }))
        const { svg } = useKitchenSlipPrint(options)

        return { label: slip.label, svg: svg.value }
      })

      previewPrinterName.value = item.attributes.name
      isPreviewOpen.value = true
    }

    const buildRoutingMap = () => {
      kitchenPrinters.value.forEach(printer => {
        const tagIds = printer.attributes.routingVendibleTagIds || []
        tagIds.forEach(tagId => {
          routingMap[tagId] = String(printer.id)
        })
      })
    }

    const saveRoutings = async () => {
      isSavingRoutings.value = true
      const routings = []
      Object.entries(routingMap).forEach(([vendibleTagId, kitchenPrinterId]) => {
        if (kitchenPrinterId) {
          routings.push({ vendibleTagId: Number(vendibleTagId), kitchenPrinterId: Number(kitchenPrinterId) })
        }
      })

      const res = await KitchenPrinterApi.updateRoutings(routings)

      if (res?.data) {
        kitchenPrinters.value = [...res.data.kitchenPrinters.data]
        if (res.data.sdpServerUrl) sdpServerUrl.value = res.data.sdpServerUrl
        vm.$toast.success('振り分け設定を保存しました')
      }

      isSavingRoutings.value = false
    }

    const getKitchenPrinters = async () => {
      const res = await KitchenPrinterApi.getKitchenPrinterSettings()
      if (res?.status === 200) {
        kitchenPrinters.value = [...res.data.kitchenPrinters.data]
        sdpServerUrl.value = res.data.sdpServerUrl || ''
        buildRoutingMap()
      }
    }

    const getVendibleTags = async () => {
      const res = await VendibleTagApi.getVendibleTags()
      if (res?.status === 200) {
        vendibleTags.value = [...res.data.vendibleTags.data]
      }
    }

    const formatDateTime = value => {
      if (!value) return null

      return new Date(value).toLocaleString('ja-JP', {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    }

    const lastPrintLabel = status => ({
      completed: '成功', failed: '失敗', sent: '送信済み', pending: '待機中',
    }[status] || '—')

    const lastPrintColor = status => ({
      completed: 'success', failed: 'error', sent: 'warning', pending: 'default',
    }[status] || 'default')

    initWith([
      getKitchenPrinters(),
      getVendibleTags(),
    ])

    return {
      isLoading,
      isPrinterFormOpen,
      isSubmitting,
      isSavingRoutings,
      testingPrinterId,
      testPrintingId,
      isPreviewOpen,
      previewSlips,
      previewPrinterName,
      kitchenPrinters,
      vendibleTags,
      routingMap,
      printerForm,
      isPrinterFormValid,
      printerSelectItems,
      sdpServerUrl,
      isIssuing,
      issuedPassword,
      isReissueConfirmOpen,
      onClickIssueSdpCredentials,
      confirmReissueSdpCredentials,
      isSdpPasswordVisible,
      issueSdpCredentials,
      copyToClipboard,
      formatDateTime,
      lastPrintLabel,
      lastPrintColor,
      printerHeaders: [
        {
          text: 'プリンター名', value: 'attributes.name',
        },
        {
          text: 'IPアドレス', value: 'attributes.ipAddress',
        },
        {
          text: '表示順', value: 'attributes.position', align: 'center',
        },
        {
          text: 'この端末で使用', value: 'enabled', align: 'center', sortable: false,
        },
        {
          text: 'SDP', value: 'sdp', align: 'center', sortable: false,
        },
        {
          text: '最終印刷', value: 'lastPrint', align: 'center', sortable: false,
        },
        {
          text: '', value: 'actions', align: 'right', sortable: false,
        },
      ],
      cancelPrinterForm,
      editPrinter,
      submitPrinter,
      destroyPrinter,
      testConnection,
      openDiagnostic,
      webConfigUrl,
      isDiagnosticOpen,
      diagnosticTarget,
      testPrint,
      showPreview,
      saveRoutings,
      isPrinterEnabled,
      connectionStatuses,
      isAutoDetecting,
      hasBrowserPrinters,
      onTogglePrinter,
      onRetestAll,
      icons: {
        mdiPlus,
        mdiAccessPointNetwork,
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiClose,
        mdiCheckCircle,
        mdiAlertCircle,
        mdiRefresh,
        mdiContentCopy,
        mdiEye,
        mdiEyeOff,
      },
    }
  },
}
</script>

<style scoped>
.preview-svg-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  background-color: #fff;
  border-radius: 8px;
}

.preview-svg-container >>> svg {
  max-width: 280px;
  height: auto;
}
</style>
